import useToast from "@/hooks/useToast";
import { ExternalLogin, ExternalLoginRequestModel } from "../types";
import { useSelector } from "react-redux";
import { RootState } from "@/store/reducers";
import { mapLocationToString, useLocation } from "@/hooks";
import { useGoogleLogin } from "@react-oauth/google";
import {
  useExternalLoginMutation,
  useGetTwitterUserInfoMutation,
  useLazyGetTwitterLoginUrlQuery,
} from "../api/login";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

type ExternalSignInResult = {
  errorMessage?: string;
  userDetail?: ExternalLoginRequestModel;
};

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: any;
  }
}

const useGoogleSignIn = () => {
  let onUserInfo: (result: ExternalSignInResult) => void = (result: any) =>
    null;

  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        const response = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${codeResponse.access_token}` },
          }
        );
        const userInfo = await response.json();
        if (typeof onUserInfo === "function")
          onUserInfo({
            userDetail: {
              firstName: userInfo.given_name,
              lastName: userInfo.name?.split(" ")[1],
              email: userInfo.email,
              profileImageUrl: userInfo.picture,
              provider: ExternalLogin.Google,
              token: userInfo.idToken,
            } as ExternalLoginRequestModel,
          });
      } catch (error: any) {
        onUserInfo({
          errorMessage: error.message,
        });
      }
    },
    onError: (error) => console.error(error),
  });

  const handleGoogleLogin = async (): Promise<ExternalSignInResult> => {
    return new Promise((resolve, reject) => {
      try {
        onUserInfo = resolve;
        login();
      } catch {
        reject({ errorMessage: "Something went wrong" });
      }
    });
  };

  return {
    handleGoogleLogin,
  };
};

const useFacebookLogin = (appId: string) => {
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);

  useEffect(() => {
    if (window.FB) {
      setIsSdkLoaded(true);
      return;
    }

    window.fbAsyncInit = () => {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: true,
        version: "v19.0", // latest version
      });

      setIsSdkLoaded(true);
    };

    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    document.body.appendChild(script);
  }, [appId]);

  const handleFbSignIn = async (): Promise<ExternalSignInResult> => {
    if (!isSdkLoaded) return { errorMessage: "SDK not loaded" };

    return new Promise((resolve, reject) => {
      window.FB.login(
        (response: any) => {
          if (response.authResponse) {
            window.FB.api(
              "/me",
              { fields: "name,email,picture" },
              (userInfo: any) => {
                return resolve({
                  userDetail: {
                    firstName: userInfo.name,
                    email: userInfo.email,
                    profileImageUrl: userInfo.picture?.data?.url,
                    provider: ExternalLogin.Facebook,
                  } as ExternalLoginRequestModel,
                });
              }
            );
          }
        },
        { scope: "public_profile,email" }
      );
    });
  };

  const handleFbLogout = () => {
    if (!isSdkLoaded) return;

    window.FB.logout(() => {});
  };

  return { handleFbSignIn, handleFbLogout };
};

const useTwitterLogin = ({
  onSuccess,
}: {
  onSuccess: (result: ExternalSignInResult) => void;
}) => {
  const [getTwitterLoginUrl] = useLazyGetTwitterLoginUrlQuery();
  const [getUserInfo] = useGetTwitterUserInfoMutation();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const code = searchParams.get("code");
        if (code) {
          const result = await getUserInfo(code).unwrap();
          if (result.userInfo) {
            const userInfo = result.userInfo;
            onSuccess({
              userDetail: {
                firstName: userInfo.name,
                profileImageUrl: userInfo?.profile_image_url,
                email: userInfo.username,
                provider: ExternalLogin.Twitter,
              } as ExternalLoginRequestModel,
            });
          }
        }
      } catch (error: any) {
        onSuccess({ errorMessage: error.message });
      }
    })();
  }, []);

  const handleTwitterLogin = async (): Promise<ExternalSignInResult> => {
    const result = await getTwitterLoginUrl().unwrap();
    if (result.twitterLoginUrl) {
      window.location.href = result.twitterLoginUrl;
      return {
        userDetail: {} as ExternalLoginRequestModel,
      };
    }

    return {
      errorMessage: "Something went wrong",
    };
  };

  return {
    handleTwitterLogin,
  };
};

const useExternalLogin = () => {
  const { showError } = useToast();
  const [externalLogin] = useExternalLoginMutation();
  const { fbAppId } =
    useSelector((state: RootState) => state.appData.appData) ?? {};

  const { getCurrentLocation } = useLocation();
  const { handleGoogleLogin } = useGoogleSignIn();
  const { handleFbSignIn } = useFacebookLogin(fbAppId);

  async function handleSignInResult(result: ExternalSignInResult) {
    if (result.errorMessage) {
      showError(result.errorMessage);
    } else if (result.userDetail) {
      externalLogin({
        ...result.userDetail,
        provider: ExternalLogin[result.userDetail.provider],
        location: mapLocationToString(await getCurrentLocation()),
      });
    } else {
      showError("Something wnet wrong");
    }
  }

  const { handleTwitterLogin } = useTwitterLogin({
    onSuccess: handleSignInResult,
  });

  const handleExternalLogin = async (value: string) => {
    let result: ExternalSignInResult = {};
    if (value == ExternalLogin[ExternalLogin.Google]) {
      result = await handleGoogleLogin();
    } else if (value == ExternalLogin[ExternalLogin.Facebook]) {
      result = await handleFbSignIn();
    } else if (value === ExternalLogin[ExternalLogin.Twitter]) {
      await handleTwitterLogin();
    }
    if (result) handleSignInResult(result);
  };

  const handleExternalLogout = async () => {
    // const credentials = await Auth.credentials;
    // if (credentials != null) {
    //   if (credentials.provider == ExternalLogin.Google && GoogleSignin)
    //     GoogleSignin.signOut();
    // }
  };

  return {
    handleExternalLogin,
    handleExternalLogout,
  };
};

export default useExternalLogin;
export {};
